<template>
  <div :class="['Accordion', { 'Accordion--footer-link': isFooterLinkVariant }]">
    <template v-if="isCollapsable">
      <ux-atoms-typo
        :as="headingTag"
        :class="['Accordion__title', { 'Accordion__title--footer-link': isFooterLinkVariant }]"
        :text="title"
        color="grey-1"
        variant="expressive-heading-05"
      >
        <button
          :aria-controls="`Accordion__content-${accordionId}`"
          :aria-expanded="isOpen"
          :class="{ 'Accordion__trigger--footer-link': isFooterLinkVariant }"
          :id="`Accordion__trigger-${accordionTriggerId}`"
          @click="Toggle"
          class="Accordion__trigger"
          type="button"
        >
          <span class="Accordion__button-title">{{ title }}</span>
          <ux-atoms-icon :class="{ 'Accordion__icon--footer-link': isFooterLinkVariant }" class="Accordion__icon" name="chevron-down" size="l" />
        </button>
      </ux-atoms-typo>
    </template>
    <template v-else>
      <ux-atoms-typo :as="headingTag" :class="{ 'Accordion__title--footer-link': isFooterLinkVariant }" :text="title" />
    </template>
    <div
      :aria-labelledby="hasArialLabelBy ? `Accordion__trigger-${accordionTriggerId}` : undefined"
      :class="['Accordion__content', { 'Accordion__content--footer-link': isFooterLinkVariant }]"
      :id="`Accordion__content-${accordionId}`"
      :role="hasRole ? 'region' : undefined"
      v-show="isOpen"
    >
      <slot name="content"></slot>
    </div>
  </div>
</template>

<script lang="ts" setup>
export interface AccordionProps {
  hasArialLabelBy?: boolean;
  hasRole?: boolean;
  isCollapsable: boolean;
  title: string;
  variant?: 'destinations' | 'footer-links';
}

const props = withDefaults(defineProps<AccordionProps>(), {
  hasArialLabelBy: true,
  hasRole: true,
  isCollapsable: false,
  title: undefined,
  variant: 'destinations'
});

interface Emits {
  (event: 'isHidden', value: boolean): void;
}

const emits = defineEmits<Emits>();

const accordionId = useId();
const accordionTriggerId = useId();
const { isDesktop } = useScreen();

const isFooterLinkVariant = computed(() => props.variant === 'footer-links');

const headingTag = computed(() => (isFooterLinkVariant.value ? 'h3' : 'h2'));

const isOpen = ref(false);

const Toggle = () => {
  const newValue = !isOpen.value;
  isOpen.value = newValue;
  emits('isHidden', !newValue);
};

watch(isDesktop, () => {
  if (isFooterLinkVariant.value) {
    isOpen.value = isDesktop.value as boolean;
  }
});
</script>
<style scoped lang="scss">
@use 'Accordion.scss';
</style>
